import { useEffect, useState } from 'react';
import { snakeCase } from 'lodash';
import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import {
  MemberTraits,
  MemberTraitsResponse,
  MemberTraitsVariables,
} from '@src/graphql/home/queries/memberTraits';

/**
 * The possible member trait fields that can be queried.
 */
export enum MemberTraitFields {
  HasApplications = 'hasApplications',
  HasApprovedApplications = 'hasApprovedApplications',
  HasBachelorsWarning = 'hasBachelorsWarning',
  HasBookmarks = 'hasBookmarks',
  HasCoachRecommendations = 'hasCoachRecommendations',
  HasCompletedApplications = 'hasCompletedApplications',
  HasDeferredApplications = 'hasDeferredApplications',
  HasQuizRecommendations = 'hasQuizRecommendations',
  HasReferrals = 'hasReferrals',
  HasStartedApplications = 'hasStartedApplications',
  IsCurrentlyEnrolled = 'isCurrentlyEnrolled',
  IsCurrentlyEnrolledInEcornell = 'isCurrentlyEnrolledInEcornell',
  IsTransitionMember = 'isTransitionMember',
  IsUnsureAboutNextStep = 'isUnsureAboutNextStep',
  TransitionProgram = 'transitionProgram { academicPartner fundingLevel programName programUuid }',
  UserUuid = 'userUuid',
}

/**
 * @name useMemberTraits
 * @description A hook that gets a set of member traits for a user.
 * @example
 * const { loading, error, traits } = useMemberTraits(
 *   [
 *     MemberTraitFields.HasStartedApplications,
 *     MemberTraitFields.HasCompletedApplications
 *   ],
 *   'ProgramsAndApplications'
 * );
 * @param memberTraitFields - The list of member trait fields to query.
 * @param queryingComponentName - The name of the component that is querying for member traits.
 * @returns An object containing loading, error, and traits properties.
 */
const useMemberTraits = (
  memberTraitFields: MemberTraitFields[],
  queryingComponentName?: string
) => {
  const { user } = useAuth();
  const [memberTraits, setMemberTraits] = useState<MemberTraits | undefined>(undefined);

  // Creates the query operation name from the calling component name if provided
  const createQueryOperationName = (componentName?: string): string => {
    return componentName
      ? `${snakeCase(componentName).toUpperCase()}_MEMBER_TRAITS_QUERY`
      : 'MEMBER_TRAITS_QUERY';
  };

  // Creates the query body with the requested member traits
  const createQueryBody = (): string => {
    return Object.values(MemberTraitFields)
      .filter((trait) => memberTraitFields.includes(trait))
      .join(' ');
  };

  // Creates the query with a custom operation name and requested fields
  const MEMBER_TRAITS_QUERY = gql`
    query ${createQueryOperationName(queryingComponentName)}($studentUuid: ID!) {
      traits: getTraits(studentUuid: $studentUuid) {
        ${createQueryBody()}
      }
    }
  `;

  // Sets the state for member traits
  const onCompleted = (data: MemberTraitsResponse): void => {
    setMemberTraits(data?.traits);
  };

  // Creates an error message for a failed member traits query
  const onError = (error: ApolloError): void => {
    const errorMessage = queryingComponentName
      ? `There was an error getting member traits for the ${queryingComponentName} component.`
      : 'There was an error getting member traits.';

    datadogRum.addError(error, { message: `${errorMessage}: ${error.message}` });
  };

  const [getMemberTraits, { loading, error }] = useLazyQuery<
    MemberTraitsResponse,
    MemberTraitsVariables
  >(MEMBER_TRAITS_QUERY, {
    context: {
      server: Server.MEMBER_TRAIT_SERVICE,
    },
    variables: {
      studentUuid: user?.id,
    },
    onCompleted,
    onError,
  });

  useEffect(() => {
    if (user?.id && !memberTraits) {
      getMemberTraits();
    }
  }, [getMemberTraits, memberTraits, user]);

  return { loading, error, memberTraits };
};

export default useMemberTraits;
